import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Router } from 'vue-router';
import type { Bidder, StoredCard, StoredMethodType } from '@neon-one/auctions-types';

// `ws.ts` imports `useSession` so we can't statically import `useWs` here
// without a cycle. signOut() does a dynamic import below.

const SESSION_KEY = 'neon-auctions:bidder-session';

// Lane B (GT1-82): payment-method snapshot. Carried on BidderSnapshot so
// the account / payment-methods management UI can read without a wire
// call.
export interface PaymentMethodSnapshot {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
  expired?: boolean;
  // GT1-488: the rail this method is on. `PaymentMethodWire.methodType` has
  // always carried it; the snapshot dropped it, which is why the account
  // surface rendered every stored method — ACH included — as a card.
  // Optional so pre-existing mock/demo snapshots keep parsing; absent is
  // treated as 'card' by the shared labeller.
  methodType?: StoredMethodType;
}

// Re-exported for the kernel barrel + admin-web fe-007. The bidder
// snapshot is a subset of the wire `Bidder` shape — we keep the snapshot
// stable across milestones so existing consumers don't churn when the
// underlying wire type widens.
export interface BidderSnapshot {
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  activated: boolean;
  hasPaymentMethod: boolean;
  storedCard: StoredCard | null;
  tcAcceptedAt: string | null;
  // Legacy alias kept for components that read activatedAt directly; M2
  // promotes the canonical field to `activated: boolean` (BidderSchema).
  activatedAt: string | null;
  // Lane B addition. Optional so existing call sites (registration,
  // activation, my-bids mock) don't break — undefined means "no list
  // available, fall back to `hasPaymentMethod` boolean."
  cards?: PaymentMethodSnapshot[];
  // Optional for the same reason as `cards`. Undefined means "not loaded
  // from /me yet" — the Account page must not render a toggle state it has
  // not read from the server, since an unsubscribe link or a T2C STOP can
  // change these without this client knowing.
  emailOptIn?: boolean;
  smsOptIn?: boolean;
}

function snapshotFromBidder(b: Bidder): BidderSnapshot {
  return {
    uuid: b.uuid,
    email: b.email,
    firstName: b.firstName,
    lastName: b.lastName,
    phone: b.phone,
    activated: b.activated,
    hasPaymentMethod: b.hasPaymentMethod,
    storedCard: b.storedCard,
    tcAcceptedAt: b.tcAcceptedAt,
    activatedAt: b.activated ? b.tcAcceptedAt ?? b.registeredAt : null,
    emailOptIn: b.emailOptIn,
    smsOptIn: b.smsOptIn,
  };
}

interface PersistedSession {
  jwt: string | null;
  bidder: BidderSnapshot | null;
  eventSlug: string | null;
}

function isJwtExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    // JWTs use Base64url (- and _ instead of + and /); normalize before atob.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof payload.exp !== 'number') return true;
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

function read(): PersistedSession {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return { jwt: null, bidder: null, eventSlug: null };
    const parsed = JSON.parse(raw) as PersistedSession;
    // Auto-clear expired real JWTs on boot (mock tokens are never expired).
    if (parsed.jwt && !parsed.jwt.startsWith('mock-jwt-') && isJwtExpired(parsed.jwt)) {
      localStorage.removeItem(SESSION_KEY);
      return { jwt: null, bidder: null, eventSlug: null };
    }
    return parsed;
  } catch {
    return { jwt: null, bidder: null, eventSlug: null };
  }
}

function write(s: PersistedSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  } catch {
    // localStorage may throw under quota or private-mode; in-memory
    // state stays authoritative.
  }
}

export const useSession = defineStore('session', () => {
  const initial = read();
  const jwt = ref<string | null>(initial.jwt);
  const bidder = ref<BidderSnapshot | null>(initial.bidder);
  const eventSlug = ref<string | null>(initial.eventSlug ?? null);

  const isAuthenticated = computed(() => jwt.value !== null);
  const isActivated = computed(() => bidder.value?.activated === true);

  function setJwt(token: string, b: BidderSnapshot | null = bidder.value, slug?: string): void {
    jwt.value = token;
    bidder.value = b;
    if (slug !== undefined) eventSlug.value = slug;
    write({ jwt: token, bidder: b, eventSlug: eventSlug.value });
  }

  function setBidder(b: BidderSnapshot | null): void {
    bidder.value = b;
    write({ jwt: jwt.value, bidder: b, eventSlug: eventSlug.value });
  }

  /**
   * Adopt a freshly-registered bidder + JWT. Called by the registration
   * feature after a successful POST /api/v1/events/:slug/registrations
   * (returns RegistrationResult { jwt, bidder }).
   */
  function adoptRegistration(input: { jwt: string; bidder: Bidder; eventSlug?: string }): void {
    setJwt(input.jwt, snapshotFromBidder(input.bidder), input.eventSlug);
  }

  /**
   * Update the cached bidder snapshot from a fresh Bidder wire payload —
   * called by the activation gate after PATCH /api/v1/me succeeds.
   */
  function refreshFromBidder(b: Bidder): void {
    setBidder(snapshotFromBidder(b));
  }

  function getJwt(): string | null {
    // The prototype writes a synthetic JWT during mock register / sign-in
    // so `isAuthenticated` flips true and route guards pass. The real
    // backend can't validate that token, so don't actually send it on
    // requests — public endpoints keep working, and protected endpoints
    // 401 cleanly without spamming "Bearer mock-jwt-..." over the wire.
    const v = jwt.value;
    if (v && v.startsWith('mock-jwt-')) return null;
    return v;
  }

  function clear(): void {
    jwt.value = null;
    bidder.value = null;
    eventSlug.value = null;
    localStorage.removeItem(SESSION_KEY);
  }

  /**
   * Lane B (GT1-82) — sign-out action. Clears the session, tears down
   * any open ws subscriptions, and (if a router is provided) navigates
   * to /:slug/sign-in so the bidder can re-authenticate. The router
   * argument is optional so unit tests can call signOut() without
   * stubbing a router; component callers always pass `useRouter()`.
   *
   * The wishlist store rekeys its localStorage entry off `session.bidder?.uuid`
   * via a watcher; signOut does NOT reach into the wishlist store so
   * the per-account entry persists for re-sign-in restoration.
   */
  async function signOut(opts: { router?: Router; slug?: string } = {}): Promise<void> {
    // Dynamic import to avoid the static cycle (`ws.ts` imports session).
    try {
      const { useWs } = await import('@/shell/stores/ws');
      useWs().disconnect();
    } catch {
      // ws not initialised yet — nothing to disconnect.
    }
    clear();
    if (opts.router && opts.slug) {
      void opts.router.push(`/${opts.slug}/sign-in`);
    }
  }

  return {
    jwt,
    bidder,
    eventSlug,
    isAuthenticated,
    isActivated,
    setJwt,
    setBidder,
    adoptRegistration,
    refreshFromBidder,
    getJwt,
    clear,
    signOut,
  };
});
